import { Link, useParams } from 'react-router-dom'
import type { CourseDetail } from '../lib/content'
import { COURSE_EXERCISES, exerciseUrl } from '../config/courseExercises'
import { FaGithub, FaArrowRight, FaArrowLeft, FaExternalLinkAlt } from 'react-icons/fa'
import { ContentColumn } from '../components/layout/ContentColumn/ContentColumn'

function CourseExercisesPage({ course }: { course: CourseDetail | null }) {
  const { slug } = useParams<{ slug: string }>()
  const set = course ? COURSE_EXERCISES[course.slug] : slug ? COURSE_EXERCISES[slug] : undefined

  if (!course || !set) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-brand-cream">
        <h1 className="font-heading text-display font-bold text-brand-navy mb-4">404</h1>
        <h2 className="font-heading text-h3 font-bold text-brand-navy mb-8">No exercises found</h2>
        <Link to="/courses" className="font-heading text-cta font-bold text-brand-navy underline">Back to Courses</Link>
      </div>
    )
  }

  // Join each exercise with its module so we can show the real module number and
  // title. Exercises are listed in their own order; the module lookup is by slug.
  const moduleBySlug = new Map(course.modules.map((m, i) => [m.slug, { title: m.title, number: i + 1 }]))

  return (
    <div className="min-h-screen bg-brand-cream">
      {/* Hero */}
      <section className="bg-brand-navy py-16 md:py-24">
        <ContentColumn anchor="course-exercises-hero" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="font-heading text-overline uppercase text-white/60">Hands-on exercises</span>
            <span className="text-white/30 font-heading text-caption" aria-hidden="true">/</span>
            <span className="font-heading text-overline uppercase text-white/60">
              {set.exercises.length} {set.noun ?? 'exercise'}{set.exercises.length === 1 ? '' : 's'}
            </span>
          </div>
          <h1 className="font-heading text-display font-bold text-white mb-6">{course.title}: exercises</h1>
          <p className="font-body text-body-lg text-white/80 mb-10 max-w-3xl">{set.intro}</p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <a
              href={set.repoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-2 bg-white text-brand-navy font-heading text-cta font-bold px-8 py-4 rounded-smooth hover:opacity-90 transition-opacity"
            >
              <FaGithub className="text-base" aria-hidden="true" />
              Open the repo
              <FaExternalLinkAlt className="text-[10px]" aria-hidden="true" />
            </a>
            <Link
              to={`/courses/${course.slug}`}
              className="inline-flex items-center gap-2 font-heading text-cta font-bold text-white/70 hover:text-white transition-colors duration-150"
            >
              <FaArrowLeft className="text-xs" aria-hidden="true" />
              Back to the course
            </Link>
          </div>
        </ContentColumn>
      </section>

      {/* Breadcrumb */}
      <div className="border-b border-border-subtle">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <nav aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 font-heading text-overline uppercase text-ink-muted">
              <li><Link to="/" className="hover:text-brand-navy transition-colors duration-150">Home</Link></li>
              <li aria-hidden="true">/</li>
              <li><Link to="/courses" className="hover:text-brand-navy transition-colors duration-150">Courses</Link></li>
              <li aria-hidden="true">/</li>
              <li><Link to={`/courses/${course.slug}`} className="hover:text-brand-navy transition-colors duration-150">{course.title}</Link></li>
              <li aria-hidden="true">/</li>
              <li className="text-brand-navy" aria-current="page">Exercises</li>
            </ol>
          </nav>
        </div>
      </div>

      {/* How it works */}
      <section className="pt-14 md:pt-16">
        <ContentColumn anchor="course-exercises-how" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border border-border-subtle rounded-smooth-lg bg-white p-6 md:p-8">
            <h2 className="font-heading text-h4 font-bold text-brand-navy mb-4 uppercase tracking-wide">How the exercises work</h2>
            <p className="font-body text-body text-ink-soft mb-4">
              {set.howItWorks ??
                'Each exercise ships a working but messy TypeScript file and a passing test suite that pins its behaviour. Your job is to refactor the code clean without turning a test red. A reference solution is checked in for every one.'}
            </p>
            {/* No colour utilities here. `text-ink-*` never generates (the tokens
                are not in @theme, see tailwind.css), so pairing it with
                bg-brand-cream left the base `pre` rule's white text on a cream
                background: invisible. The base rule is the site's code block. */}
            <pre className="overflow-x-auto"><code>{(
              set.commands ??
              `git clone {repo}.git
pnpm install
pnpm test        # run every module
pnpm test 03     # run one module`
            ).replace('{repo}', set.repoUrl)}</code></pre>
          </div>
        </ContentColumn>
      </section>

      {/* Exercise cards */}
      <section className="py-14 md:py-16">
        <ContentColumn anchor="course-exercises-list" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-3">
            {set.exercises.map((ex) => {
              const mod = moduleBySlug.get(ex.moduleSlug)
              return (
                <a
                  key={ex.moduleSlug}
                  href={exerciseUrl(set.repoUrl, ex)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 md:gap-5 border border-border-subtle rounded-smooth-lg bg-white p-5 md:p-6 transition-colors duration-150 hover:bg-brand-cream/50 group"
                >
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand-navy text-white font-heading text-caption font-bold">
                    {mod?.number ?? ''}
                  </span>
                  <div className="flex-1 min-w-0">
                    {mod && (
                      <span className="block font-heading text-overline uppercase tracking-wider text-ink-muted mb-1">{mod.title}</span>
                    )}
                    <h3 className="font-heading text-body font-bold text-brand-navy group-hover:underline decoration-brand-navy/40 decoration-1 underline-offset-2">
                      {ex.title}
                    </h3>
                    <p className="font-body text-body-sm text-ink-soft mt-1.5">{ex.summary}</p>
                    <span className="mt-3 inline-flex items-center gap-2 font-heading text-caption font-bold text-brand-navy">
                      <FaGithub className="text-sm" aria-hidden="true" />
                      Open on GitHub
                      <FaArrowRight className="text-[10px] transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden="true" />
                    </span>
                  </div>
                </a>
              )
            })}
          </div>
        </ContentColumn>
      </section>
    </div>
  )
}

export default CourseExercisesPage
